import {
  each,
  find,
  isArray,
  isEqual,
  includes,
  merge,
  replace,
  split,
} from 'lodash'
import { getFullBoundsFromArray } from '@/services/location'
import mapApi from '@/api/endpoints/map'
import store from '@/store/index'

async function clearState() {
  await store.dispatch('filter/clearFilters', [{}], { root: true })
  await store.dispatch('location/clearSelectedLocation', [{}], { root: true })
  await store.dispatch('garden/clearState', [{}], { root: true })
  await store.dispatch('map/clearState', [{}], { root: true })
}

const routes = [
  {
    path: '/',
    name: 'Search',
    component: () => import(/* webpackChunkName: "search" */ '@/views/pages/Search'),
    meta: {
      nav: true,
      header: 'SimpleHeader',
    },
    async beforeEnter(to, from, next) {
      if (!from?.name) {
        await clearState();
      }

      next();
    },
  },
  {
    path: '/garden-list',
    name: 'List',
    component: () => import(/* webpackChunkName: "list" */ '@/views/pages/List'),
    meta: {
      nav: true,
      header: 'Header',
    },
    async beforeEnter(to, from, next) {
      if (!from?.name) {
        await clearState();
      }

      if (to.query && from?.name !== 'Search') {
        await store.dispatch('map/toggleRouteSetup', false, { root: true })

        // Get all of the currently Set filters
        const selectedFiltersQuery = store.getters['filter/selectedFiltersQuery']
        const selectedFiltersQueryCheck = store.getters['filter/selectedFiltersQueryCheck']
        const searchTerm = store.getters['garden/searchTerm']
        const selectedSearchType = store.getters['garden/selectedSearchType']
        const searchBounds = store.getters['map/searchBoundsQuery']
        const selectedSort = store.getters['sort/selectedSortValue']

        // Build up a query param from the currently set filters in order to check if they match the url params
        let queryFilters = {}
        if (searchTerm) {
          queryFilters = merge(queryFilters, { search_term: searchTerm })
        }
        if (searchBounds) {
          queryFilters = merge(queryFilters, { search_bounds: searchBounds })
        }
        if (selectedSort) {
          queryFilters = merge(queryFilters, { sort_by: selectedSort })
        }

        if (selectedFiltersQuery) {
          queryFilters = merge(queryFilters, selectedFiltersQueryCheck)
        }

        // Check if the current params match the url params
        if (!isEqual(to.query, queryFilters)) {
          // If not equal then make sure dynamicFilterSections are available so
          // the url filters below can be matched against them.
          let dynamicFilterSections = store.getters['filter/dynamicFilterSections']
          const hadSections = !!dynamicFilterSections
          try {
            if (hadSections) {
              // Already have a cached copy (possibly stale). Use it immediately
              // and let ensureFilters refresh it in the background if the TTL has
              // lapsed or the version changed — don't block navigation on it.
              store.dispatch('filter/ensureFilters', null, { root: true }).catch(() => {})
            } else {
              // Cold cache (first visit or cleared): must fetch before we can map
              // url filters, then seed the default selected filters.
              await store.dispatch('filter/ensureFilters', null, { root: true })
              await store.dispatch('filter/resetFilters', [{}], { root: true })
            }
            dynamicFilterSections = store.getters['filter/dynamicFilterSections']
          } catch (e) {
            console.log('Could not fetch filters')
          }

          // Run through all of the url filters and set them as current set filters
          each(to.query, function (urlQueryFilter, urlQueryFilterKey) {
            let isntSearch = false
            if (!includes(['search_term', 'search_bounds', 'sort_by'], urlQueryFilterKey)) {
              isntSearch = true
            }

            if (isntSearch) {
              const filterKey = replace(replace(urlQueryFilterKey, 'filter[', ''), ']', '')

              // Set the filters that are arrays
              if (isArray(urlQueryFilter)) {
                each(urlQueryFilter, function (itemToAdd) {
                  // FILTERS ONLY GET SET IF THEY MATCH ONE OF THE DYNAMIC FILTERS FETCHED FROM THE API
                  const matchingDynamicFilterSection = dynamicFilterSections ? find(dynamicFilterSections, function (dynamicFilterSection) {
                    return dynamicFilterSection.filter === filterKey && find(dynamicFilterSection.options, ['value', itemToAdd])
                  }) : null
                  // `updateTag` toggles a checkbox/buttons option off on re-application,
                  // so skip when it's already selected — otherwise URL re-hydration
                  // (e.g. after back navigation) silently drops the filter.
                  if (store.getters['filter/filterSelected'](filterKey, itemToAdd)) {
                    return
                  }
                  store.dispatch(
                    'filter/updateTag',
                    {
                      filter: filterKey,
                      type: matchingDynamicFilterSection.display_type,
                      options: [itemToAdd],
                    },
                    { root: true },
                  )
                })
              }

              // Set the filters that are NOT arrays
              if (!isArray(urlQueryFilter)) {
                const matchingDynamicFilterSection = dynamicFilterSections ? find(dynamicFilterSections, function (dynamicFilterSection) {
                  return dynamicFilterSection.filter === filterKey && find(dynamicFilterSection.options, ['value', urlQueryFilter])
                }) : null

                // FILTERS ONLY GET SET IF THEY MATCH ONE OF THE DYNAMIC FILTERS FETCHED FROM THE API (except for geo_bounding_box, date_open_from & date_open_to)
                if (matchingDynamicFilterSection) {
                  if (includes(['checkbox', 'buttons'], matchingDynamicFilterSection.display_type)) {
                    // See array branch above — skip re-applying an already-selected
                    // option to avoid `updateTag`'s toggle-off behaviour.
                    if (!store.getters['filter/filterSelected'](filterKey, urlQueryFilter)) {
                      store.dispatch(
                        'filter/updateTag',
                        {
                          filter: filterKey,
                          type: matchingDynamicFilterSection.display_type,
                          options: [urlQueryFilter],
                        },
                        { root: true },
                      )
                    }
                  }

                  if (includes(['radio', 'defaulted_toggle', 'multi_button'], matchingDynamicFilterSection.display_type)) {
                    store.dispatch(
                      'filter/updateTag',
                      {
                        filter: filterKey,
                        type: matchingDynamicFilterSection.display_type,
                        options: urlQueryFilter,
                        initial: true,
                      },
                      { root: true },
                    )
                  }
                } else {
                  if (filterKey === 'geo_bounding_box') {
                    store.dispatch('filter/updateTag', { filter: filterKey, type: 'text', options: urlQueryFilter }, { root: true })
                  }
                  if (filterKey === 'date_open_from' && to.query['filter[date_open_to]']) {
                    const toUrlQueryfilter = to.query['filter[date_open_to]']
                    store.dispatch('filter/updateTag',
                      {
                        filter: 'days_open',
                        type: 'date_range',
                        options: {
                          date_open_from: urlQueryFilter,
                          date_open_to: toUrlQueryfilter,
                        },
                      }, { root: true })
                  }
                }
              }
            }
          })

          if (to.query.search_term || to.query.search_bounds) {
            if (to.query.search_term) {
              if (to.query['filter[garden_name]']) {
                if (to.query['filter[garden_name]'] === to.query.search_term) {
                  if (selectedSearchType.value !== 'garden') {
                    store.dispatch('garden/updateSearchType', store.getters['garden/gardenSearchType'], { root: true })
                  }
                  store.dispatch('filter/updateTag', { filter: 'garden', type: 'text', options: to.query.search_term }, { root: true })
                } else {
                  store.dispatch('filter/updateTag', { filter: 'garden_name', type: 'text', options: to.query['filter[garden_name]'] }, { root: true })
                }
              }
              store.dispatch('garden/updateSearchTerm', to.query.search_term, { root: true })
            }
            store.dispatch('garden/clearSearchResults', [{}], { root: true })

            if (to.query.search_bounds) {
              if (selectedSearchType.value !== 'location') {
                store.dispatch('garden/updateSearchType', store.getters['garden/locationSearchType'], { root: true })
              }
              const searchBoundsLngLat = split(to.query.search_bounds, ',')
              const bounds = getFullBoundsFromArray(searchBoundsLngLat)

              store.dispatch('map/addSearchBounds', bounds, { root: true })

              const boundsFilter = {
                filter: 'geo_bounding_box',
                type: 'text',
                options: to.query.search_bounds,
              }

              store.dispatch('filter/updateTag', boundsFilter, { root: true })
            }
          }

          // SORTING
          if (to.query.sort_by) {
            if (to.query.sort_by !== selectedSort) {
              const sortByOption = find(store.getters['sort/sortOptions'], ['label', to.query.sort_by])
              store.dispatch('sort/updateSort', sortByOption, { root: true })
            }
          } else {
            if (selectedSort) {
              store.dispatch('sort/clearState', { root: true })
            }
          }

          // When the URL carries a search_term but we have no selectedLocation in state yet
          // (direct URL entry, bookmark, reload), geocode the term so we can pick the correct
          // radius from location_type — otherwise the radius stays at the 10 mile default.
          if (to.query.search_term && !store.getters['location/selectedLocation']) {
            try {
              const response = await mapApi.location({ address: to.query.search_term })
              const result = response?.data?.results?.[0]
              if (result) {
                await store.dispatch('location/autoSelectRadiusBasedOnLocationType', result, { root: true })
                // Store the location so buildCountyFilterParams emits place_id (else a direct
                // county URL draws no polygon); plain mutation avoids the action's reset/re-nav.
                store.commit('location/setSelectedLocation', result, { root: true })
              }
            } catch (e) {
              console.log('Could not geocode search_term for auto-radius', e)
            }
          }
        }
        store.dispatch('map/toggleRouteSetup', true, { root: true })
      }

      next();
    },
  },
  {
    path: '/garden/:gardenId/:gardenName',
    name: 'Garden',
    //  route level code-splitting
    // this generates a separate chunk (methodInformation.[hash].js) for this route
    //  which is lazy-loaded when the route is visited.
    component: () => import(/* webpackChunkName: "garden" */ '@/views/pages/Garden'),
    meta: {
      nav: true,
      header: 'SimpleHeader',
    },
  },
]

export default routes
